import useSWR, { mutate } from "swr";
import { instance } from "@/frontend/lib/hc";
import { useSession } from "@/frontend/lib/auth";

const BASE_URL = "/api/alert-history";

// Fetcher function for SWR
const fetcher = async (url: string) => {
  const response = await instance.get(url).json();
  return response;
};

interface TriggeringMessage {
  sender: string;
  message: string;
  timestamp?: string;
}

interface AlertTrigger {
  id: string;
  alertId: string;
  alertName: string;
  alertCondition: string;
  conversationId: string;
  conversationName: string;
  summaryDate: string;
  alertReason: string;
  triggeringMessages: TriggeringMessage[];
  triggeredAt: string;
  notificationSent: boolean;
  notificationChannels: string[];
  notificationError: string | null;
  read: boolean;
}

interface AlertHistoryResponse {
  triggers: AlertTrigger[];
  count: number;
  total: number;
  unreadCount: number;
  limit: number;
  offset: number;
}

/**
 * Hook to get alert history
 * Automatically filters by current user's ID - members see only their own alerts,
 * editors and admins see all alerts in the organization
 */
export function useAlertHistory(params?: {
  organizationId?: string;
  limit?: number;
  offset?: number;
}) {
  const { data: session } = useSession();

  const searchParams = new URLSearchParams();
  if (params?.organizationId) searchParams.set("organizationId", params.organizationId);
  if (session?.user?.id) searchParams.set("userId", session.user.id);
  if (params?.limit) searchParams.set("limit", params.limit.toString());
  if (params?.offset) searchParams.set("offset", params.offset.toString());

  const url = params?.organizationId && session?.user?.id
    ? `${BASE_URL}?${searchParams.toString()}`
    : null;

  return useSWR<AlertHistoryResponse>(url, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30000, // 30 seconds
  });
}

/**
 * Hook to get a specific alert trigger
 * Automatically filters by current user's ID - members can only access their own alerts,
 * editors and admins can access any alert in the organization
 */
export function useAlertTrigger(id: string | null, organizationId?: string) {
  const { data: session } = useSession();

  const searchParams = new URLSearchParams();
  if (organizationId) searchParams.set("organizationId", organizationId);
  if (session?.user?.id) searchParams.set("userId", session.user.id);

  const url = id && organizationId && session?.user?.id
    ? `${BASE_URL}/${id}?${searchParams.toString()}`
    : null;

  return useSWR<AlertTrigger>(url, fetcher, {
    revalidateOnFocus: false,
  });
}

/**
 * Function to mark an alert trigger as read
 */
export async function markAlertAsRead(
  triggerId: string,
  userId: string,
  organizationId: string
): Promise<{ success: boolean }> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set("userId", userId);
    searchParams.set("organizationId", organizationId);

    const response = await instance.patch(
      `${BASE_URL}/${triggerId}/read?${searchParams.toString()}`
    ).json();

    // Revalidate the alert history list to show updated read status
    mutate((key) => typeof key === 'string' && key.startsWith(BASE_URL));

    return { success: true };
  } catch (error) {
    console.error("Error marking alert as read:", error);
    return { success: false };
  }
}

/**
 * Function to mark all alert triggers as read
 */
export async function markAllAlertsAsRead(
  userId: string,
  organizationId: string
): Promise<{ success: boolean }> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set("userId", userId);
    searchParams.set("organizationId", organizationId);

    const response = await instance.patch(
      `${BASE_URL}/mark-all-read?${searchParams.toString()}`
    ).json();

    // Revalidate the alert history list to show updated read status
    mutate((key) => typeof key === 'string' && key.startsWith(BASE_URL));

    return { success: true };
  } catch (error) {
    console.error("Error marking all alerts as read:", error);
    return { success: false };
  }
}
